/**
 * Parceiros indicadores do orçamento — arquiteto, decorador (M-ORC-1).
 *
 * O CRUD existia no backend e os 4 wrappers existiam aqui no frontend,
 * sem nenhum consumidor: dava para escolher parceiro na CRIAÇÃO do
 * orçamento e nunca mais. Trocar o arquiteto, corrigir um RT digitado
 * errado ou adicionar o decorador que entrou depois exigia refazer a
 * proposta inteira.
 *
 * O RT dos parceiros é somado e vira a comissão do negócio — por isso
 * este bloco só aparece enquanto o orçamento ainda é negociável. Depois
 * de aprovado, mexer aqui mudaria a comissão de um negócio fechado e a
 * proposta que o cliente já recebeu passaria a dizer outra coisa.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiPlus, FiUsers, FiX } from "react-icons/fi";

import AsyncButton from "../../components/AsyncButton";
import { useAuth } from "../../context/AuthContext";
import { listarRepresentantes } from "../../services/comercial";
import {
  adicionarParceiroAoOrcamento,
  atualizarParceiroDoOrcamento,
  listarParceirosDoOrcamento,
  removerParceiroDoOrcamento,
} from "../../services/orcamentos";
import { formatarDinheiro, parseDinheiro } from "../../utils/dinheiro";

import { Card, EmptyMsg, InfoMsg, Input } from "./styles";

export default function ParceirosBloco({ orcamento, editavel, onAlterado }) {
  const { request } = useAuth();

  const [parceiros, setParceiros] = useState([]);
  const [disponiveis, setDisponiveis] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [escolhido, setEscolhido] = useState("");
  const [pct, setPct] = useState("");

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const p = await listarParceirosDoOrcamento(request, orcamento.id);
      setParceiros(Array.isArray(p) ? p : p?.results || []);
    } catch (_) {
      setParceiros([]);
    } finally {
      setCarregando(false);
    }
  }, [request, orcamento.id]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    if (!editavel) return;
    listarRepresentantes(request, { pageSize: 200 }, { silentError: true })
      .then((p) => {
        const lista = Array.isArray(p) ? p : p?.results || [];
        setDisponiveis(lista.filter((r) => r?.ativo !== false));
      })
      .catch(() => setDisponiveis([]));
  }, [request, editavel]);

  const jaVinculados = useMemo(
    () => new Set(parceiros.map((p) => String(p.representante))),
    [parceiros],
  );

  const opcoes = useMemo(
    () => disponiveis.filter((r) => !jaVinculados.has(String(r.id))),
    [disponiveis, jaVinculados],
  );

  // O RT do negócio é a SOMA — arquiteto 5% + decorador 3% = 8%.
  const totalRt = parceiros.reduce(
    (s, p) => s + (Number(p.comissao_percentual) || 0), 0,
  );

  const adicionar = async () => {
    if (!escolhido) throw new Error("Escolha o parceiro.");
    const rep = disponiveis.find((r) => String(r.id) === String(escolhido));
    const valor = parseDinheiro(pct);
    await adicionarParceiroAoOrcamento(request, {
      orcamento: orcamento.id,
      representante: escolhido,
      // Sem digitar, herda o RT do cadastro do parceiro.
      comissao_percentual: String(
        valor != null ? valor : rep?.comissao_percentual ?? 0,
      ),
    });
    setEscolhido("");
    setPct("");
    await carregar();
    onAlterado?.();
  };

  const alterarPct = async (p, texto) => {
    const valor = parseDinheiro(texto);
    if (valor == null) return;
    await atualizarParceiroDoOrcamento(request, p.id, {
      comissao_percentual: String(valor),
    });
    await carregar();
    onAlterado?.();
  };

  const remover = async (p) => {
    await removerParceiroDoOrcamento(request, p.id);
    await carregar();
    onAlterado?.();
  };

  return (
    <Card>
      <h3 style={{ display: "flex", alignItems: "center", gap: 8, margin: "0 0 4px" }}>
        <FiUsers size={15} aria-hidden="true" /> Parceiros indicadores
      </h3>
      <InfoMsg style={{ marginBottom: 12 }}>
        O RT de todos é somado e vira a comissão deste negócio
        {totalRt > 0 ? ` — hoje ${formatarDinheiro(totalRt)}% no total` : ""}.
      </InfoMsg>

      {carregando ? (
        <EmptyMsg>Carregando…</EmptyMsg>
      ) : parceiros.length === 0 ? (
        <EmptyMsg>
          Nenhum parceiro neste orçamento. Sem parceiro, a comissão cai no
          representante padrão do cliente.
        </EmptyMsg>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {parceiros.map((p) => (
            <div
              key={p.id}
              style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "8px 10px", border: "1px solid var(--border)",
                borderRadius: 6,
              }}
            >
              <span style={{ flex: 1, minWidth: 0 }}>
                <strong style={{ display: "block" }}>
                  {p.representante_nome || "Parceiro"}
                </strong>
                {p.observacao ? (
                  <span style={{ fontSize: 11, opacity: 0.7 }}>{p.observacao}</span>
                ) : null}
              </span>
              {editavel ? (
                <>
                  <Input
                    style={{ width: 90, textAlign: "right" }}
                    defaultValue={formatarDinheiro(p.comissao_percentual)}
                    aria-label={`RT de ${p.representante_nome}`}
                    onBlur={(e) => alterarPct(p, e.target.value)}
                  />
                  <span style={{ opacity: 0.7 }}>%</span>
                  <AsyncButton
                    variant="icon"
                    silentError
                    successMessage="Parceiro removido."
                    onClick={() => remover(p)}
                    aria-label={`Remover ${p.representante_nome}`}
                  >
                    <FiX size={14} aria-hidden="true" />
                  </AsyncButton>
                </>
              ) : (
                <span style={{ fontWeight: 600 }}>
                  {formatarDinheiro(p.comissao_percentual)}%
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {editavel ? (
        <div style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "center" }}>
          <select
            className="input"
            style={{ flex: 1 }}
            value={escolhido}
            onChange={(e) => setEscolhido(e.target.value)}
          >
            <option value="">— Adicionar parceiro —</option>
            {opcoes.map((r) => (
              <option key={r.id} value={r.id}>
                {r.nome} ({formatarDinheiro(r.comissao_percentual)}%)
              </option>
            ))}
          </select>
          <Input
            style={{ width: 90, textAlign: "right" }}
            placeholder="RT %"
            value={pct}
            onChange={(e) => setPct(e.target.value)}
          />
          <AsyncButton
            variant="secondary"
            onClick={adicionar}
            successMessage="Parceiro adicionado."
            errorMessage="Não foi possível adicionar."
          >
            <FiPlus size={13} aria-hidden="true" /> Adicionar
          </AsyncButton>
        </div>
      ) : (
        <InfoMsg style={{ marginTop: 12 }}>
          O orçamento não está mais em negociação — o RT ficou travado com o
          negócio. Para mudar, abra uma revisão.
        </InfoMsg>
      )}
    </Card>
  );
}
